// EM2c Hex Grid Math — Flat-top hexagonal grid system
// Based on redblobgames hex guide, adapted for flat-top orientation

// --- Constants ---
export const HEX_SIZE = 3; // world units — 3 = ~6 unit wide hex, fits 4x4 buildings // world units from center to vertex

// --- Direction definitions (flat-top) ---
// 6 neighbor directions in axial coordinates
export const HEX_DIRECTIONS = [
  { q: 1, r: 0 },   // 0: East
  { q: 1, r: -1 },  // 1: NE
  { q: 0, r: -1 },  // 2: NW
  { q: -1, r: 0 },  // 3: West
  { q: -1, r: 1 },  // 4: SW
  { q: 0, r: 1 },   // 5: SE
];

// Edge angles in degrees (flat-top hex)
export const EDGE_ANGLES = [0, 60, 120, 180, 240, 300];

// --- Coordinate conversions ---
// Axial (q, r) to world (x, z) — flat-top hex
export function axialToWorld(q, r) {
  const x = HEX_SIZE * (3 / 2) * q;
  const z = HEX_SIZE * (Math.sqrt(3) / 2 * q + Math.sqrt(3) * r);
  return { x, z };
}

// World (x, z) to axial (q, r) — flat-top hex picking
export function worldToAxial(x, z) {
  const q = (2 / 3) * x / HEX_SIZE;
  const r = (-1 / 3 * x + Math.sqrt(3) / 3 * z) / HEX_SIZE;
  return hexRound(q, r);
}

// Round fractional axial coords to nearest hex (using cube coordinates)
export function hexRound(qFrac, rFrac) {
  const sFrac = -qFrac - rFrac;
  
  let q = Math.round(qFrac);
  let r = Math.round(rFrac);
  let s = Math.round(sFrac);
  
  const qDiff = Math.abs(q - qFrac);
  const rDiff = Math.abs(r - rFrac);
  const sDiff = Math.abs(s - sFrac);
  
  if (qDiff > rDiff && qDiff > sDiff) {
    q = -r - s;
  } else if (rDiff > sDiff) {
    r = -q - s;
  }
  
  return { q, r };
}

// --- Cube coordinate helpers ---
export function axialToCube(q, r) {
  return { x: q, y: -q - r, z: r };
}

export function cubeToAxial(x, y, z) {
  return { q: x, r: z };
}

// --- Distance ---
export function hexDistance(q1, r1, q2, r2) {
  const cube1 = axialToCube(q1, r1);
  const cube2 = axialToCube(q2, r2);
  return (Math.abs(cube1.x - cube2.x) + Math.abs(cube1.y - cube2.y) + Math.abs(cube1.z - cube2.z)) / 2;
}

// --- Neighbors ---
export function getNeighbors(q, r) {
  return HEX_DIRECTIONS.map(d => ({ q: q + d.q, r: r + d.r }));
}

// Get neighbor in a specific direction (0-5)
export function getNeighborInDirection(q, r, direction) {
  const d = HEX_DIRECTIONS[direction % 6];
  return { q: q + d.q, r: r + d.r };
}

// --- Rotation ---
// Rotate a footprint (array of {q, r} offsets) by 60° clockwise * n times
// Rotation formula: (q, r) -> (-r, q + r)
export function rotateFootprint(footprint, rotations) {
  const n = ((rotations % 6) + 6) % 6; // normalize to 0-5
  if (n === 0) return footprint;
  
  let result = footprint.map(h => ({ q: h.q, r: h.r }));
  for (let i = 0; i < n; i++) {
    result = result.map(({ q, r }) => ({ q: -r, r: q + r }));
  }
  return result;
}

// --- Grid generation ---
export function generateGrid(radius) {
  const grid = new Map();
  for (let q = -radius; q <= radius; q++) {
    const r1 = Math.max(-radius, -q - radius);
    const r2 = Math.min(radius, -q + radius);
    for (let r = r1; r <= r2; r++) {
      const pos = axialToWorld(q, r);
      grid.set(`${q},${r}`, {
        q,
        r,
        x: pos.x,
        z: pos.z,
        height: 0,
        slope: 0,
        occupied: false,
        buildingId: null,
        buildable: true,
        hazard: false,
        dust: 0,
      });
    }
  }
  return grid;
}

// --- Edge direction helpers ---
export function getEdgeAngle(direction) {
  return EDGE_ANGLES[direction % 6];
}

export function getEdgeDirection(from, to) {
  // Returns the direction index (0-5) from one hex to an adjacent hex
  const dq = to.q - from.q;
  const dr = to.r - from.r;
  for (let i = 0; i < 6; i++) {
    if (HEX_DIRECTIONS[i].q === dq && HEX_DIRECTIONS[i].r === dr) return i;
  }
  return -1; // not adjacent
}

// --- Line drawing (for pathfinding/debug) ---
export function hexLine(q1, r1, q2, r2) {
  const n = hexDistance(q1, r1, q2, r2);
  const results = [];
  for (let i = 0; i <= n; i++) {
    const t = n === 0 ? 0 : i / n;
    const qFrac = q1 + (q2 - q1) * t;
    const rFrac = r1 + (r2 - r1) * t;
    results.push(hexRound(qFrac, rFrac));
  }
  return results;
}

// --- Hex area/range ---
export function hexesInRange(q, r, range) {
  const results = [];
  for (let dq = -range; dq <= range; dq++) {
    for (let dr = Math.max(-range, -dq - range); dr <= Math.min(range, -dq + range); dr++) {
      results.push({ q: q + dq, r: r + dr });
    }
  }
  return results;
}

// --- Hex geometry helpers for Three.js ---
export function getHexVertices(q, r, size = HEX_SIZE) {
  const center = axialToWorld(q, r);
  const vertices = [];
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 180) * (60 * i);
    vertices.push({
      x: center.x + size * Math.cos(angle),
      z: center.z + size * Math.sin(angle),
    });
  }
  return vertices;
}

// Get the midpoint of a specific edge (for connector placement)
export function getEdgeMidpoint(q, r, edgeDir, size = HEX_SIZE) {
  const center = axialToWorld(q, r);
  const angle1 = (Math.PI / 180) * (60 * edgeDir);
  const angle2 = (Math.PI / 180) * (60 * (edgeDir + 1));
  return {
    x: center.x + size * (Math.cos(angle1) + Math.cos(angle2)) / 2,
    z: center.z + size * (Math.sin(angle1) + Math.sin(angle2)) / 2,
  };
}
